import { useInfiniteQuery, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Board } from '../models/board';
import { BoardType } from '../models/boardType';
import { BoardIdentifierNumber } from '../models/boardUserBisk';
import BiskService from '../services/bisk.service';
import UserService from '../services/user.service';
import { isUUID } from '../services/utils';
import { getAdjustedTiming, QueryTiming } from './useQueryConfig';

// Extended interface with pre-fetched user display fields
export interface EnrichedBoardResult extends BoardIdentifierNumber {
    display_username: string;
    display_profile_pic: string;
}

// Enrich a single board result with user data
const enrichBoardResult = async (result: BoardIdentifierNumber): Promise<EnrichedBoardResult> => {
    let display_username = '';
    let display_profile_pic = '/assets/photos/defaultProPic.png';

    try {
        let user;
        if (isUUID(result.id)) {
            user = await UserService.getUserPublicById(result.id);
        } else {
            user = await UserService.getUserByUsername(result.id);
        }

        if (user?.username) {
            display_username = user.username;
            if (user.profile?.profile_pic) {
                display_profile_pic = user.profile.profile_pic;
            }
        }
    } catch (e) {
        console.error('Failed to fetch user for board result:', e);
    }

    return {
        ...result,
        display_username,
        display_profile_pic,
    };
};

// Batch enrich board results - exported for potential reuse
export const enrichBoardResults = async (results: BoardIdentifierNumber[]): Promise<EnrichedBoardResult[]> => {
    return Promise.all(results.map(enrichBoardResult));
};

export const useBoardResults = () => {
    const [boardSelected, setBoardSelected] = useState<string>('');
    const [boardPeriodSelected, setBoardPeriodSelected] = useState<string>('');
    const [boardSortSelected, setBoardSortSelected] = useState<string>('');
    const [boardFullSelected, setBoardFullSelected] = useState<Board | null>(null);
    const queryClient = useQueryClient();
    const numberOfBoardTransactions = 15;

    const { data: boards = [] } = useQuery({
        queryKey: ['boards'],
        queryFn: BiskService.getBoards,
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY),
    });

    useEffect(() => {
        if (boards.length > 0 && !boardSelected) {
            setBoardSelected(boards[0].name);
            setBoardPeriodSelected(boards[0].periods[0]);
            setBoardSortSelected(boards[0].sort[0]);
            setBoardFullSelected(boards[0]);
        }
    }, [boards, boardSelected]);

    const fetchBoardResults = async ({ pageParam = 0 }): Promise<EnrichedBoardResult[] | BoardIdentifierNumber[]> => {
        const results = await BiskService.getBoardResult(boardSelected, boardPeriodSelected, boardSortSelected, numberOfBoardTransactions, pageParam);

        // Only enrich user-type boards (not IDX_INFO or USER_INFO which display raw data)
        if (boardFullSelected &&
            boardFullSelected.board_type !== BoardType.IDX_INFO &&
            boardFullSelected.board_type !== BoardType.USER_INFO) {
            return enrichBoardResults(results);
        }

        return results;
    };

    const {
        data,
        fetchNextPage,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        status,
        error,
        refetch
    } = useInfiniteQuery({
        queryKey: ['boardResults', boardSelected, boardPeriodSelected, boardSortSelected],
        queryFn: fetchBoardResults,
        initialPageParam: 0,
        // Since boards update daily
        ...getAdjustedTiming(QueryTiming.LOW_FREQUENCY),
        getNextPageParam: (lastPage, allPages) => {
            return lastPage.length === numberOfBoardTransactions ? allPages.length : undefined;
        },
        enabled: !!boardSelected && !!boardPeriodSelected && !!boardSortSelected,
    });

    const boardResults: (EnrichedBoardResult | BoardIdentifierNumber)[] = useMemo(() =>
        data?.pages.flat() || [],
        [data]
    );

    const handleBoardChange = useCallback((newBoard: string) => {
        const selectedBoard = boards.find((board: Board) => board.name === newBoard);
        if (selectedBoard) {
            setBoardSelected(newBoard);
            setBoardPeriodSelected(selectedBoard.periods[0]);
            setBoardSortSelected(selectedBoard.sort[0]);
            setBoardFullSelected(selectedBoard);
        }
    }, [boards]);

    const handleBoardPeriodChange = useCallback((newPeriod: string) => {
        setBoardPeriodSelected(newPeriod);
    }, []);

    const handleBoardSortChange = useCallback((newSort: string) => {
        setBoardSortSelected(newSort);
    }, []);

    const getMoreBoardResults = useCallback(() => {
        if (hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
        }
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    const resetBoardRefresh = useCallback(async () => {
        await queryClient.resetQueries({ queryKey: ['boardResults', boardSelected, boardPeriodSelected, boardSortSelected] });
        await refetch();
    }, [refetch]);

    return {
        boards,
        boardSelected,
        boardPeriodSelected,
        boardSortSelected,
        boardFullSelected,
        boardResults,
        hasNextPage,
        isFetching,
        isFetchingNextPage,
        status,
        error,
        handleBoardChange,
        handleBoardPeriodChange,
        handleBoardSortChange,
        getMoreBoardResults,
        refetch: resetBoardRefresh
    };
};
